import {
  showSearchAtom,
  useFeatureFlags,
  useIsSupport,
} from "@granular/fabric3-core";
import { useAtom } from "jotai";
import type React from "react";
import { lazy, Suspense, useEffect } from "react";

const LazySearchModal = lazy(() => import("@granular/fabric3-search-library"));

/**
 * Very lightweight wrapper around the search modal from the fabric search
 * library. Do not add any complex logic here. This should only be handling
 * opening/closing the modal. All search-specific stuff should be in the
 * library.
 */
export const SearchImplementation: React.FC = () => {
  const flags = useFeatureFlags();
  const isSearchEnabled = !!flags["support-search-tool"];

  const isSupport = useIsSupport();
  const [showSearch, setShowSearch] = useAtom(showSearchAtom);

  const isEnabled = isSupport && isSearchEnabled;

  // 🎹 Keyboard shortcut: Cmd+K to toggle search modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "k") {
        event.preventDefault();
        setShowSearch((prev) => !prev);
      } else if (event.key === "Escape") {
        setShowSearch(false);
      }
    };

    if (isEnabled) {
      globalThis.addEventListener("keydown", handleKeyDown);
      return () => globalThis.removeEventListener("keydown", handleKeyDown);
    }
  }, [isEnabled, setShowSearch]);

  return isEnabled ? (
    <Suspense fallback={undefined}>
      <LazySearchModal show={showSearch} toggle={() => setShowSearch(false)} />
    </Suspense>
  ) : undefined;
};
